/* ═══════════════════════════════════════════════════════════════
   The Designer — revision marks on the paper.

   SpecForma's track changes, not Word's: WHAT changed is always
   computed — a word-level diff of the live document against the active
   baseline snapshot (the latest manual issuance's frozen copy of this
   section). Nothing here is ever persisted; edit the paragraph back and
   its mark disappears.

   HOW changes look is the architect's punch-in settings (migration 033
   `revision_settings`): strike/underline styles, highlight, print
   color, change bars — the same settings the .docx export renders, so
   the marks on screen ARE the marks on paper.

   Everything renders as DECORATIONS on top of the document:
     · added paragraphs — the whole node styled in the "new text" mark
     · modified paragraphs — inline marks on inserted runs, plus small
       struck widgets where words were deleted
     · deleted paragraphs — non-editable struck widgets at the position
       the paragraph used to occupy
     · margin change bars beside anything changed
   Off in design phase (no baseline yet): the plugin renders nothing.
   ═══════════════════════════════════════════════════════════════ */

import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { EditorState, Transaction } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { ContentBlock, RevisionMarkStyle, RevisionSettings } from '../../shared/types';
import { buildInlineDiff } from '../services/specService';
import { BLOCK_NODE_NAMES } from './commands';

export const revisionsPluginKey = new PluginKey('designerRevisions');

/** Ask the plugin to rebuild after the baseline or the settings changed. */
export const REBUILD_REVISIONS = 'designer:rebuildRevisions';

/** Word's highlighter palette — content colors that print, mirroring the
 *  export exactly (spec-docx-builder). Not UI chrome. */
const WORD_HIGHLIGHT_CSS: Record<string, string> = {
  yellow: '#FFFF00',
  green: '#00FF00',
  cyan: '#00FFFF',
  magenta: '#FF00FF',
  gray: '#C0C0C0',
};

/** One mark style as CSS text for a decoration attribute. Pure mirror of the
 *  export's run properties — these values PRINT, they are not theme chrome. */
export function markCssText(s: RevisionMarkStyle | undefined): string {
  if (!s) return '';
  const parts: string[] = [];
  const deco = [
    s.strikethrough ? 'line-through' : '',
    s.underline && s.underline !== 'none' ? 'underline' : '',
  ]
    .filter(Boolean)
    .join(' ');
  if (deco) {
    parts.push(`text-decoration-line:${deco}`);
    parts.push(`text-decoration-style:${s.underline === 'double' ? 'double' : 'solid'}`);
  }
  const hl = s.highlight && s.highlight !== 'none' ? WORD_HIGHLIGHT_CSS[s.highlight] : null;
  if (hl) parts.push(`background-color:${hl}`);
  if (s.color) parts.push(`color:#${s.color.replace('#', '')}`);
  if (s.bold) parts.push('font-weight:700');
  return parts.join(';');
}

export interface RevisionsOptions {
  /** The baseline block list (already flattened, excluded filtered). Null =
   *  design phase / no baseline — the plugin renders nothing. */
  getBaseline: () => ContentBlock[] | null;
  /** The project's punch-in mark settings, read at decoration time. */
  getSettings: () => RevisionSettings;
}

/** The live document as the diff engine's block shape. Excluded blocks are
 *  skipped — they never print, so they carry no marks (they already render
 *  struck by the exclusion styling). */
function docBlocks(state: EditorState): Array<{
  block: ContentBlock;
  pos: number;
  nodeSize: number;
  isParagraph: boolean;
}> {
  const out: Array<{
    block: ContentBlock;
    pos: number;
    nodeSize: number;
    isParagraph: boolean;
  }> = [];
  state.doc.forEach((node, offset) => {
    if (!BLOCK_NODE_NAMES.includes(node.type.name)) return;
    if (node.attrs.excluded) return;
    out.push({
      block: {
        id: (node.attrs.blockId as string) || '',
        level: (node.attrs.level as number) || 1,
        text: node.type.name === 'specParagraph' ? node.textContent : '',
        ...(node.type.name === 'specTable' ? { blockType: 'table' as const } : {}),
        ...(node.type.name === 'specImage' ? { blockType: 'image' as const } : {}),
        ...(node.type.name === 'smartBlock' ? { blockType: 'smart-token' as const } : {}),
      },
      pos: offset,
      nodeSize: node.nodeSize,
      isParagraph: node.type.name === 'specParagraph',
    });
  });
  return out;
}

/** A deleted paragraph, shown where it used to live: not editable, not
 *  selectable content — a widget the diff re-derives on every change. */
function removedWidget(
  text: string,
  deletedCss: string,
  changeBar: boolean,
  blockType?: string
): HTMLElement {
  const div = document.createElement('div');
  div.className = 'dsg-rev-removed' + (changeBar ? ' dsg-rev-bar' : '');
  div.setAttribute('contenteditable', 'false');
  // The paragraph no longer exists in the document — announce it as struck
  // context, not as editable content.
  div.setAttribute('aria-label', 'Deleted paragraph');
  const span = document.createElement('span');
  span.style.cssText = deletedCss;
  span.textContent =
    blockType === 'table'
      ? '[deleted table]'
      : blockType === 'image'
        ? '[deleted figure]'
        : text || ' ';
  div.appendChild(span);
  return div;
}

/** A run of words deleted inside a surviving paragraph. */
function deletedRunWidget(text: string, deletedCss: string): HTMLElement {
  const span = document.createElement('span');
  span.className = 'dsg-rev-del';
  span.setAttribute('contenteditable', 'false');
  span.setAttribute('aria-label', 'Deleted text');
  span.style.cssText = deletedCss;
  span.textContent = text;
  return span;
}

function buildDecorations(
  state: EditorState,
  baseline: ContentBlock[] | null,
  settings: RevisionSettings
): DecorationSet {
  if (!baseline || settings.showMarks === false) return DecorationSet.empty;

  const live = docBlocks(state);
  const diff = buildInlineDiff(
    baseline.filter((b) => !b.excluded),
    live.map((l) => l.block)
  );
  const byId = new Map(live.map((l) => [l.block.id, l]));

  // A baseline paragraph that still exists on the paper but is now marked
  // "skip at export" reads as removed to the diff — but it is already
  // rendered struck by the exclusion styling, so the deleted-paragraph
  // widget would show it twice.
  const excludedOnPaper = new Set<string>();
  state.doc.forEach((node) => {
    if (BLOCK_NODE_NAMES.includes(node.type.name) && node.attrs.excluded) {
      excludedOnPaper.add((node.attrs.blockId as string) || '');
    }
  });
  const bars = settings.changeBars !== false;
  const addedCss = markCssText(settings.added);
  const deletedCss = markCssText(settings.deleted);

  const decorations: Decoration[] = [];

  // Walk the interleaved diff. A removed paragraph anchors to the start of
  // the next surviving block (or the document's end), which IS the position
  // it previously occupied.
  for (let i = 0; i < diff.length; i++) {
    const d = diff[i];

    if (d.kind === 'removed') {
      if (excludedOnPaper.has(d.id)) continue;
      let anchor = state.doc.content.size;
      for (let j = i + 1; j < diff.length; j++) {
        const nextLive = diff[j].kind !== 'removed' ? byId.get(diff[j].id) : undefined;
        if (nextLive) {
          anchor = nextLive.pos;
          break;
        }
      }
      const text = d.oldText || '';
      decorations.push(
        Decoration.widget(anchor, () => removedWidget(text, deletedCss, bars, d.blockType), {
          side: -1,
          key: `rev-removed-${d.id}`,
        })
      );
      continue;
    }

    const entry = byId.get(d.id);
    if (!entry) continue;

    if (d.kind === 'added') {
      decorations.push(
        Decoration.node(entry.pos, entry.pos + entry.nodeSize, {
          class: 'dsg-rev-added' + (bars ? ' dsg-rev-bar' : ''),
          style: addedCss,
        })
      );
      continue;
    }

    if (d.kind === 'modified') {
      decorations.push(
        Decoration.node(entry.pos, entry.pos + entry.nodeSize, {
          class: bars ? 'dsg-rev-bar' : 'dsg-rev-modified',
        })
      );
      // Word-level marks only make sense inside prose. A modified table or
      // figure gets the paragraph-level treatment (change bar) only.
      if (entry.isParagraph && d.wordDiff) {
        let offset = 0; // character offset in the NEW text
        let runIdx = 0;
        for (const tok of d.wordDiff) {
          if (tok.op === 'keep') {
            offset += tok.value.length;
          } else if (tok.op === 'add') {
            const from = entry.pos + 1 + offset;
            const to = from + tok.value.length;
            decorations.push(
              Decoration.inline(from, to, { class: 'dsg-rev-ins', style: addedCss })
            );
            offset += tok.value.length;
          } else {
            // Deleted words render where they used to sit, struck.
            const value = tok.value;
            decorations.push(
              Decoration.widget(entry.pos + 1 + offset, () => deletedRunWidget(value, deletedCss), {
                side: -1,
                key: `rev-del-${d.id}-${runIdx}`,
              })
            );
          }
          runIdx++;
        }
      }
    }
  }

  return DecorationSet.create(state.doc, decorations);
}

/**
 * The revisions plugin. Rebuilds when the document changes or when a
 * REBUILD_REVISIONS transaction announces a baseline/settings change; maps
 * positions otherwise. With no baseline it costs one function call per
 * transaction and renders nothing.
 */
export const Revisions = Extension.create<RevisionsOptions>({
  name: 'designerRevisions',

  addOptions() {
    return {
      getBaseline: () => null,
      getSettings: () => ({ showMarks: true, added: {}, deleted: {} }),
    };
  },

  addProseMirrorPlugins() {
    const getBaseline = () => this.options.getBaseline();
    const getSettings = () => this.options.getSettings();
    return [
      new Plugin({
        key: revisionsPluginKey,
        state: {
          init: (_config, state) => buildDecorations(state, getBaseline(), getSettings()),
          apply: (tr: Transaction, value: DecorationSet, _old, newState) => {
            if (tr.docChanged || tr.getMeta(REBUILD_REVISIONS)) {
              return buildDecorations(newState, getBaseline(), getSettings());
            }
            return value.map(tr.mapping, tr.doc);
          },
        },
        props: {
          decorations(state) {
            return revisionsPluginKey.getState(state) as DecorationSet;
          },
        },
      }),
    ];
  },
});
